import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, PackagePlus, Plus, X } from 'lucide-react';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import { trpc } from '@/lib/trpc';
import { toast } from 'sonner';
import { DeviceFormDialog } from './DeviceFormDialog';
import { usePagination } from '@/hooks/usePagination';

export interface QuipteamsSuggestion {
  serialNumber: string;
  orderId: string;
  categoria: string;
  modelo: string;
  descripcion?: string;
  procesador?: string;
  ramGB?: number | null;
  discoGB?: number | null;
  owner: string;
  pais: string;
  deviceType: string;
  status: string;
  fechaCompra?: string;
  qtCreatedAt?: string;
  condition?: string;
}

interface Props {
  suggestions: QuipteamsSuggestion[];
  isLoading: boolean;
  error: string | null;
  onAdded: () => void;
}

export function QuipteamsSuggestionsTab({ suggestions, isLoading, error, onAdded }: Props) {
  const { t } = useTranslation();
  const [prefill, setPrefill] = useState<QuipteamsSuggestion | null>(null);
  const [declineTarget, setDeclineTarget] = useState<QuipteamsSuggestion | null>(null);

  const declineMutation = trpc.inventory.declineQuipteamsSuggestion.useMutation({
    onSuccess: () => {
      toast.success(t('devices.inventory_page.quipteams.declined_toast'));
      setDeclineTarget(null);
      onAdded();
    },
    onError: (err) => toast.error(err.message),
  });
  // Paginado (antes de los returns tempranos): QuipTeams puede traer decenas de equipos
  // sin cargar; el conteo del intro sigue sobre el total
  const { pageItems: pageSuggestions, footer: paginationFooter } = usePagination(suggestions, 25);

  if (isLoading) {
    return (
      <Card className="flex items-center justify-center p-12">
        <WinclapLoader size={48} className="text-foreground/25" />
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="flex flex-col items-center justify-center p-12 space-y-3">
        <AlertCircle className="h-10 w-10 text-red-500" />
        <p className="font-semibold">{t('devices.inventory_page.quipteams.error_title')}</p>
        <p className="text-sm text-muted-foreground text-center max-w-md">{error}</p>
      </Card>
    );
  }

  if (suggestions.length === 0) {
    return (
      <Card className="flex flex-col items-center justify-center p-12 space-y-3">
        <PackagePlus className="h-10 w-10 text-muted-foreground" />
        <p className="font-semibold">{t('devices.inventory_page.quipteams.empty_title')}</p>
        <p className="text-sm text-muted-foreground">{t('devices.inventory_page.quipteams.empty_desc')}</p>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <div className="p-4 border-b">
          <p className="text-sm text-muted-foreground">
            {t('devices.inventory_page.quipteams.intro', { count: suggestions.length })}
          </p>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('devices.inventory_page.quipteams.col_serial')}</TableHead>
                <TableHead>{t('devices.inventory_page.quipteams.col_type')}</TableHead>
                <TableHead>{t('devices.inventory_page.quipteams.col_model')}</TableHead>
                <TableHead>{t('devices.inventory_page.quipteams.col_employee')}</TableHead>
                <TableHead>{t('devices.inventory_page.quipteams.col_country')}</TableHead>
                <TableHead>{t('devices.inventory_page.quipteams.col_qt_date')}</TableHead>
                <TableHead>{t('devices.inventory_page.quipteams.col_condition')}</TableHead>
                <TableHead className="text-right">{t('devices.inventory_page.quipteams.col_action')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pageSuggestions.map((s) => (
                <TableRow key={s.serialNumber}>
                  <TableCell className="font-mono text-sm">{s.serialNumber}</TableCell>
                  <TableCell className="text-sm">{s.deviceType || '-'}</TableCell>
                  <TableCell className="text-sm">{s.modelo || '-'}</TableCell>
                  <TableCell className="text-sm">{s.owner || '-'}</TableCell>
                  <TableCell className="text-sm">{s.pais || '-'}</TableCell>
                  <TableCell className="text-sm tabular-nums">{s.qtCreatedAt || '-'}</TableCell>
                  <TableCell>
                    {s.condition ? (
                      <Badge variant="outline" className="text-xs">{s.condition}</Badge>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button size="sm" onClick={() => setPrefill(s)}>
                        <Plus className="mr-1.5 h-4 w-4" />
                        {t('devices.inventory_page.quipteams.add')}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-muted-foreground hover:text-destructive"
                        onClick={() => setDeclineTarget(s)}
                      >
                        <X className="mr-1.5 h-4 w-4" />
                        {t('devices.inventory_page.quipteams.decline')}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {paginationFooter && <div className="px-4 pb-3">{paginationFooter}</div>}
      </Card>

      <DeviceFormDialog
        open={!!prefill}
        onOpenChange={(open) => {
          if (!open) {
            setPrefill(null);
            onAdded();
          }
        }}
        initialValues={
          prefill
            ? {
                nroSerie: prefill.serialNumber,
                categoria: prefill.categoria,
                modelo: prefill.modelo,
                procesador: prefill.procesador || '',
                ramGB: (prefill.ramGB ?? '') as any,
                discoGB: (prefill.discoGB ?? '') as any,
                owner: prefill.owner,
                // Status del INVENTARIO (lista cerrada) — el de QT es otro vocabulario
                status: prefill.owner ? 'Entregado' : 'Lista para Entregar',
                fechaCompra: prefill.fechaCompra || '',
                comments: [
                  prefill.descripcion ? `QuipTeams: ${prefill.descripcion}` : '',
                  prefill.condition ? `Condición: ${prefill.condition}` : '',
                ].filter(Boolean).join(' · '),
                quipteamsOrderId: prefill.orderId,
              }
            : undefined
        }
      />

      <AlertDialog open={!!declineTarget} onOpenChange={(open) => !open && setDeclineTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('devices.inventory_page.quipteams.decline_confirm_title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('devices.inventory_page.quipteams.decline_confirm_desc', {
                serial: declineTarget?.serialNumber,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('devices.delete_dialog.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              disabled={declineMutation.isPending}
              onClick={() =>
                declineTarget && declineMutation.mutate({ serialNumber: declineTarget.serialNumber })
              }
            >
              {t('devices.inventory_page.quipteams.decline')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
